import React, { useCallback, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  MenuItem,
  FormControl,
  Select,
  ListItemText,
  Checkbox,
  InputBase,
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  IconButton
} from '@material-ui/core';
import renderHTML from 'react-render-html';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core/styles';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { menuList } from '../constant';
import useStyles from '../style';
import { debounce, keysIn, isEmpty, get } from '../../../utils/lodash';
import { localeMoment as moment } from '../../common/moment';
import { DateRangePickerComponent } from 'materialui-daterange-picker';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';

const ListText = withStyles(theme => ({
  root: {
    fontSize: 14,
    '& *': {
      fontSize: 14
    }
  }
}))(ListItemText);

const SelectInput = withStyles(theme => ({
  root: { width: '100%' },
  input: {
    borderRadius: theme.spacing(1),
    border: '1px solid #5D5A5A',
    padding: `${theme.spacing(0, 2)} !important`,
    lineHeight: 2,
    height: '100%',
    color: '#5D5A5A',
    fontSize: 12,
    '&:focus': { borderRadius: theme.spacing(1), backgroundColor: theme.palette.primary.white }
  }
}))(InputBase);

const installDateList = [
  {
    key: 'today',
    text: 'Today'
  },
  {
    key: 'past_7_days',
    text: 'Past 7 Days'
  },
  {
    key: 'month_date',
    text: 'Month to Date'
  },
  {
    key: 'year_date',
    text: 'Year to Date'
  },
  {
    key: 'specific_date',
    text: 'Specific Date'
  },
  {
    key: 'all_dates_before',
    text: 'All Dates Before'
  },
  {
    key: 'all_dates_after',
    text: 'All Dates After'
  },
  {
    key: 'date_range',
    text: 'Date Range'
  },
  {
    key: 'clear_dates',
    text: 'Clear Dates'
  }
];

export default function SystemTable(props) {
  const { data, selectedColumns, message, loading, updateState, updateColumns, isMobileView, selectedSiteId } = props;
  const classes = useStyles(props);
  const [ isDateRangeOpen, setIsDateRangeOpen ] = useState(false);
  const [ dateRange, setDateRange ] = useState({});
  const [ enableButton, setEnableButton ] = useState(false);
  const [ selectedDate, setSelectedDate ] = useState('');
  const [ selectSpecificDateOpen, setSelectSpecificDateOpen ] = useState(false);
  const [ enableSelectedButton, setEnableSelectedButton ] = useState(false);
  const [ isAllDaysBefore, setIsAllDaysBefore ] = useState(false);
  const [ isAllDaysAfter, setIsAllDaysAfter ] = useState(false);
  const [ showInstallDateOption, setShowInstallDateOption ] = useState(false);
  const [ installDateFilterText, setInstallDateFilterText ] = useState('');
  const debounceInput = useCallback(debounce(value => updateState(value, true), 1000), []);
  const [ sortColumnDetails, setSortColumnDetails ] = useState({});

  const handleSortList = column => {
    const currentSort = get(sortColumnDetails, `${column}`);
    const sort = isEmpty(currentSort) ? 'asc' : currentSort === 'asc' ? 'desc' : 'asc';
    const sortColumnDetail = { [column]: sort };
    setSortColumnDetails({ ...sortColumnDetail });
    updateState({}, true, 1, sortColumnDetail);
  };

  const handleInstallDateChange = event => {
    let value = event.target.value;
    let showDatePicker = false;
    const selectedInstallDate = installDateList.find(installDate => installDate.key === value);
    let filterText = get(selectedInstallDate, 'text', '') || '';
    if (value === 'clear_dates') {
      value = '';
      const installedDate = moment(new Date()).subtract(2, 'years').format('M/D/YY');
      filterText = `${installedDate} – `;
    } else if (value === 'date_range') {
      showDatePicker = true;
      setIsDateRangeOpen(true);
    } else if ([ 'specific_date', 'all_dates_before', 'all_dates_after' ].includes(value)) {
      showDatePicker = true;
      setSelectSpecificDateOpen(true);
      if (value === 'all_dates_before') {
        setIsAllDaysBefore(true);
      } else if (value === 'all_dates_after') {
        setIsAllDaysAfter(true);
      } else {
        setIsAllDaysBefore(false);
        setIsAllDaysAfter(false);
      }
    }
    if (!showDatePicker && !(selectedInstallDate.key === 'clear_dates' && isEmpty(installDateFilterText))) {
      debounceInput({ install_date: filterText });
      const installDateFilterText = value === '' ? '' : get(selectedInstallDate, 'text', '');
      setInstallDateFilterText(installDateFilterText);
    }
  };

  const handleDateRangeChange = range => {
    setDateRange(range);
    !enableButton && setEnableButton(true);
  };

  const handleCloseDateRange = () => {
    setIsDateRangeOpen(false);
    setEnableButton(false);
    setDateRange({});
  };

  const handleSaveDateRange = () => {
    setIsDateRangeOpen(false);
    setEnableButton(false);
    const { startDate, endDate } = dateRange;
    const modifiedStartDate = moment(startDate).format('M/D/YY');
    const modifiedEndDate = moment(endDate).format('M/D/YY');
    const filterText = `${modifiedStartDate}+–+${modifiedEndDate}`;
    const installDateFilterText = `${modifiedStartDate} - ${modifiedEndDate}`;
    setInstallDateFilterText(installDateFilterText);
    debounceInput({ install_date: filterText });
  };

  const handleSaveSelectedDate = () => {
    setSelectSpecificDateOpen(false);
    setEnableSelectedButton(false);
    setIsAllDaysBefore(false);
    setIsAllDaysAfter(false);
    let filterText = `${moment(selectedDate).format('M/D/YY')}`;
    let installDateFilterText = filterText;
    if (isAllDaysBefore) {
      filterText = `+–+${filterText}`;
      installDateFilterText = ` - ${installDateFilterText}`;
    } else if (isAllDaysAfter) {
      filterText = `${filterText}+–+`;
      installDateFilterText = `${installDateFilterText} - `;
    }
    setInstallDateFilterText(installDateFilterText);
    debounceInput({ install_date: filterText });
  };

  const handleCloseSelectedDate = () => {
    setSelectSpecificDateOpen(false);
    setEnableSelectedButton(false);
    setIsAllDaysBefore(false);
    setIsAllDaysAfter(false);
    setSelectedDate('');
  };

  const handleDateChange = date => {
    setSelectedDate(date);
    !enableSelectedButton && setEnableSelectedButton(true);
  };

  const toggle = () => setIsDateRangeOpen(!isDateRangeOpen);

  const getStatusBasedOnCode = () => {
    return {
      storage_idle: 'Storage Inactive',
      battery: 'AC Batteries Not Reporting',
      power: 'Production Issue',
      micro: 'Microinverters Not Reporting',
      meter: 'Meter Issue',
      encharge: 'IQ Battery Not Reporting',
      enpower: 'IQ System Controller Not Reporting',
      comm: 'Gateway Not Reporting',
      any: 'Any Problem',
      normal: 'Normal'
    };
  };

  return (
    <Box className={clsx(classes.root, classes.systemRoot)}>
      <Box className={clsx(classes.systemHeaderWrapper, isMobileView && classes.mobileSystemHeader)}>
        <Typography className={classes.systemTableHeading}>Systems</Typography>
        <FormControl
          variant="outlined"
          className={clsx(classes.formControl, isMobileView && classes.mobileFormControl)}
        >
          <Select
            labelId="demo-mutiple-checkbox-label"
            id="demo-mutiple-checkbox"
            multiple
            value={selectedColumns}
            onChange={updateColumns}
            input={<SelectInput />}
            IconComponent={() => ''}
            variant="outlined"
            renderValue={() => 'Manage Table Columns'}
          >
            {keysIn(menuList).map(key => {
              if (![ 'id' ].includes(key)) {
                const isChecked = selectedColumns.indexOf(key) > -1;
                const isDisabled = selectedColumns.length >= 8 && !isChecked;
                return (
                  <MenuItem classes={classes.menuList} key={key} value={key} disabled={isDisabled}>
                    <Checkbox color="primary" checked={isChecked} />
                    <ListText primary={menuList[key]} />
                  </MenuItem>
                );
              }
              return null;
            })}
          </Select>
        </FormControl>
      </Box>
      <Paper elevation={3} className={classes.elevation}>
        <TableContainer className={classes.systemTableContainer}>
          <Table aria-label="sticky table">
            <TableHead>
              <TableRow>
                {selectedColumns.map(column => (
                  <TableCell
                    className={clsx(classes.header, menuList[column].length > 17 && classes.largeText)}
                    key={column}
                  >
                    {menuList[column]}
                    <IconButton className={classes.sortIconButton} onClick={() => handleSortList(column)}>
                      {isEmpty(get(sortColumnDetails, `${column}`)) ? (
                        <UnfoldMoreIcon />
                      ) : get(sortColumnDetails, `${column}`) === 'asc' ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {selectedColumns.map(column => {
                  let additionalProps = {};
                  if (column === 'system_id' && !isEmpty(selectedSiteId)) {
                    additionalProps = { defaultValue: selectedSiteId };
                  }
                  if (column === 'address') {
                    return <TableCell className={clsx(classes.header, classes.searchHeader)} key={column} />;
                  } else if (column === 'install_date') {
                    return (
                      <TableCell className={clsx(classes.header, classes.searchHeader)} key={column}>
                        <Typography
                          component="div"
                          className={classes.installDateField}
                          onClick={() => setShowInstallDateOption(true)}
                        >
                          {installDateFilterText}
                        </Typography>
                        <Select
                          open={showInstallDateOption}
                          onClose={() => setShowInstallDateOption(false)}
                          className={classes.installDateDropdown}
                          variant="outlined"
                          value={''}
                          onChange={e => handleInstallDateChange(e)}
                        >
                          {installDateList.map((installDate, index) => (
                            <MenuItem key={index} value={installDate.key}>
                              {installDate.text}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell className={clsx(classes.header, classes.searchHeader)} key={column}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        className={classes.inputField}
                        onChange={e => debounceInput({ [column]: e.target.value })}
                        variant="outlined"
                        size="small"
                        {...additionalProps}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                !loading &&
                data.map((systemData, systemIndex) => (
                  <TableRow className={classes.tableRow} tabIndex={-1} key={systemIndex}>
                    {selectedColumns.map(key => {
                      const displayValue =
                        key === 'status'
                          ? getStatusBasedOnCode()[systemData[key]] || 'All'
                          : key === 'this_month_est_pct' && systemData[key]
                            ? renderHTML(systemData[key])
                            : systemData[key];
                      return (
                        <TableCell className={classes.tableData} key={key} title={systemData[key]}>
                          {displayValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              {loading && (
                <TableRow>
                  <TableCell colSpan={selectedColumns.length} className={classes.loader}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
              {data &&
              data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={selectedColumns.length}>No results found</TableCell>
                </TableRow>
              )}
              {message && (
                <TableRow>
                  <TableCell colSpan={selectedColumns.length} className={classes.err}>
                    {message}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* <Box className={classes.paginationFooter}>
        <Box className={classes.pageLimit}>
          <Typography>Show</Typography>
          <Select className={classes.limitDropdown} variant="outlined" value={limit} onChange={e => handlePageLimit(e)}>
            {limitList.map((limitValue, index) => (
              <MenuItem key={index} value={limitValue}>
                {limitValue}
              </MenuItem>
            ))}
          </Select>
          <Typography>Branches</Typography>
        </Box>
        {noOfPages > 1 && (
          <Pagination
            className={classes.paginationContainer}
            count={noOfPages}
            defaultPage={page}
            onChange={handlePagination}
            showFirstButton
            showLastButton
          />
        )}
      </Box> */}
      <Dialog open={isDateRangeOpen} onClose={handleCloseDateRange}>
        <DialogContent className={classes.dateRangeWrapper}>
          <DateRangePickerComponent
            open={isDateRangeOpen}
            definedRanges={[]}
            toggle={toggle}
            onChange={range => handleDateRangeChange(range)}
            closeOnClickOutside
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            disabled={!enableButton}
            className={classes.okButton}
            onClick={handleSaveDateRange}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={selectSpecificDateOpen} onClose={handleCloseSelectedDate}>
        <DialogContent className={classes.dateRangeWrapper}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              disableToolbar
              variant="static"
              format="MM/dd/yyyy"
              margin="normal"
              id="date-picker-inline"
              label="Date picker inline"
              value={selectedDate}
              onChange={handleDateChange}
              KeyboardButtonProps={{
                'aria-label': 'change date'
              }}
            />
          </MuiPickersUtilsProvider>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            disabled={!enableSelectedButton}
            className={classes.okButton}
            onClick={handleSaveSelectedDate}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
